import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { generateVideoThumbnail, dataURLtoFile } from '@/utils/videoThumbnail';

interface Restaurant {
  id: string;
  name: string;
}

interface MenuItem {
  id: string;
  name: string;
}

interface PostCreationProps {
  mediaFiles: File[];
  onClose: () => void;
  onBack: () => void;
}

const PostCreation: React.FC<PostCreationProps> = ({ mediaFiles, onClose, onBack }) => {
  const [caption, setCaption] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState<string>('');
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>('');
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [isPosting, setIsPosting] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    fetchRestaurants();
  }, []);

  useEffect(() => {
    if (selectedRestaurant) {
      fetchMenuItems(selectedRestaurant);
    } else {
      setMenuItems([]);
      setSelectedMenuItem('');
    }
  }, [selectedRestaurant]);

  const fetchRestaurants = async () => {
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('id, name')
        .eq('is_open', true)
        .order('name');

      if (error) throw error;
      setRestaurants(data || []);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      toast({
        title: "Error",
        description: "Failed to load restaurants",
        variant: "destructive",
      });
    }
  };

  const fetchMenuItems = async (restaurantId: string) => {
    try {
      const { data, error } = await supabase
        .from('menu_items')
        .select('id, name')
        .eq('restaurant_id', restaurantId)
        .eq('is_available', true)
        .order('name');

      if (error) throw error;
      setMenuItems(data || []);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast({
        title: "Error",
        description: "Failed to load menu items",
        variant: "destructive",
      });
    }
  };

  const uploadMediaFiles = async (files: File[], ownerId: string): Promise<{ urls: string[], thumbnailUrl?: string }> => {
    let thumbnailUrl: string | null = null;
    
    const uploadPromises = files.map(async (file) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${ownerId}/${fileName}`;

      // If it's a video, generate and upload thumbnail
      if (file.type.startsWith('video/') && !thumbnailUrl) {
        try {
          const thumbnailDataUrl = await generateVideoThumbnail(file);
          const thumbnailFile = dataURLtoFile(thumbnailDataUrl, `thumb_${fileName.replace(/\.[^/.]+$/, '.jpg')}`);
          const thumbnailPath = `${ownerId}/${thumbnailFile.name}`;
          
          const { data: thumbData, error: thumbError } = await supabase.storage
            .from('post-media')
            .upload(thumbnailPath, thumbnailFile);

          if (!thumbError) {
            const { data: { publicUrl } } = supabase.storage
              .from('post-media')
              .getPublicUrl(thumbData.path);
            thumbnailUrl = publicUrl;
          }
        } catch (error) {
          console.error('Error generating thumbnail:', error);
        }
      }

      const { data, error } = await supabase.storage
        .from('post-media')
        .upload(filePath, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('post-media')
        .getPublicUrl(data.path);

      return publicUrl;
    });

    const urls = await Promise.all(uploadPromises);
    return { urls, thumbnailUrl };
  };

  const handlePost = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to post",
        variant: "destructive",
      });
      return;
    }

    if (mediaFiles.length === 0) {
      toast({
        title: "Error",
        description: "Please select media to post",
        variant: "destructive",
      });
      return;
    }

    if (!selectedRestaurant) {
      toast({
        title: "Error",
        description: "Please select a restaurant",
        variant: "destructive",
      });
      return;
    }

    if (!selectedMenuItem) {
      toast({
        title: "Error",
        description: "Please select a menu item",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsPosting(true);

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session?.user) {
        throw sessionError || new Error('No active session');
      }

      const ownerId = session.user.id;

      // Upload media files
      const { urls: mediaUrls, thumbnailUrl } = await uploadMediaFiles(mediaFiles, ownerId);

      // Determine content type
      const contentType = mediaFiles[0].type.startsWith('video/') ? 'video' : 'image';

      // Create post
      const { error } = await supabase
        .from('posts')
        .insert({
          user_id: ownerId,
          content_type: contentType,
          media_urls: mediaUrls,
          thumbnail_url: thumbnailUrl,
          caption: caption.trim() || null,
          restaurant_id: selectedRestaurant || null,
          menu_item_id: selectedMenuItem || null,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Post created successfully!",
      });

      onClose();
    } catch (error) {
      console.error('Error creating post:', error);
      toast({
        title: "Error",
        description: "Failed to create post",
        variant: "destructive",
      });
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="text-white hover:bg-white/20"
        >
          <ArrowLeft className="h-6 w-6" />
        </Button>
        
        <h1 className="text-white text-lg font-semibold">New Post</h1>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-white hover:bg-white/20"
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Media Preview */}
        <div className="grid grid-cols-3 gap-2">
          {mediaFiles.map((file, index) => {
            console.log('Media file:', file.name, file.type, file.size);
            return (
              <div key={index} className="aspect-square">
                {file.type.startsWith('video/') ? (
                  <video
                    src={URL.createObjectURL(file)}
                    className="w-full h-full object-cover rounded-lg"
                    controls
                    playsInline
                    muted
                    onLoadedData={() => console.log('Video loaded successfully')}
                    onError={(e) => console.error('Video preview error:', e)}
                    onCanPlay={() => console.log('Video can play in preview')}
                  />
                ) : (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Media ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg"
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Caption */}
        <div className="space-y-2">
          <label className="text-white text-sm font-medium">Caption</label>
          <Textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Write a caption..."
            className="bg-gray-900 border-gray-700 text-white placeholder-gray-400 min-h-[100px]"
            maxLength={500}
          />
          <div className="text-gray-400 text-xs text-right">
            {caption.length}/500
          </div>
        </div>

        {/* Restaurant Selection */}
        <div className="space-y-2">
          <label className="text-white text-sm font-medium">Restaurant *</label>
          <Select value={selectedRestaurant} onValueChange={setSelectedRestaurant}>
            <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
              <SelectValue placeholder="Select a restaurant" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              {restaurants.map((restaurant) => (
                <SelectItem key={restaurant.id} value={restaurant.id} className="text-white">
                  {restaurant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Menu Item Selection */}
        {selectedRestaurant && (
          <div className="space-y-2">
            <label className="text-white text-sm font-medium">Menu Item *</label>
            <Select value={selectedMenuItem} onValueChange={setSelectedMenuItem}>
              <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                <SelectValue placeholder="Select a menu item" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                {menuItems.map((item) => (
                  <SelectItem key={item.id} value={item.id} className="text-white">
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Post Button */}
      <div className="p-4 border-t border-gray-800">
        <Button
          onClick={handlePost}
          disabled={isPosting || mediaFiles.length === 0 || !selectedRestaurant || !selectedMenuItem}
          className="w-full bg-red-600 hover:bg-red-700 text-white py-3 disabled:opacity-50"
        >
          {isPosting ? 'Posting...' : 'Post'}
        </Button>
      </div>
    </div>
  );
};

export default PostCreation;